/**
 * KNK-1.12 / FR-19 — the Door sheet's quick buttons, for the Account and per team.
 *
 * Five slots and More (§A10). A team either uses the Account's set or has its own, whole: a team
 * set replaces the Account's for its members rather than merging slot by slot, which is what
 * `knock_quick_buttons_for_me()` serves the phone.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  QUICK_BUTTONS_COPY as COPY,
  QUICK_SLOTS,
  quickButtonSetFor,
  validateQuickButtons,
  type Disposition,
  type QuickButtonRow,
} from '@/lib/knockDispositions';
import type { TeamOption } from '@/services/knockDispositionService';

const ACCOUNT = 'account';
const EMPTY = '__empty__';

const pad = (codes: readonly string[]) => [...codes, ...Array(QUICK_SLOTS).fill('')].slice(0, QUICK_SLOTS);

export interface QuickButtonsCardProps {
  dispositions: readonly Disposition[];
  rows: readonly QuickButtonRow[];
  teams: readonly TeamOption[];
  onSave: (teamId: number | null, codes: string[]) => Promise<void>;
}

export function QuickButtonsCard({ dispositions, rows, teams, onSave }: QuickButtonsCardProps) {
  const [scope, setScope] = useState<string>(ACCOUNT);
  const teamId = scope === ACCOUNT ? null : Number(scope);
  const saved = quickButtonSetFor(rows, teamId);
  const accountSet = quickButtonSetFor(rows, null) ?? [];

  // A team without its own set is shown the account's, read-only, until someone customises it.
  const [customising, setCustomising] = useState(false);
  const [slots, setSlots] = useState<string[]>(pad(saved ?? accountSet));
  const [busy, setBusy] = useState(false);

  const savedKey = (saved ?? []).join(',');
  useEffect(() => {
    setSlots(pad(saved ?? accountSet));
    setCustomising(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on scope or saved change only
  }, [scope, savedKey, accountSet.join(',')]);

  const choosable = useMemo(
    () => dispositions.filter((d) => d.active && d.category !== 'Do Not Knock'),
    [dispositions],
  );
  const byCode = useMemo(() => new Map(dispositions.map((d) => [d.code, d])), [dispositions]);

  const editing = teamId === null || saved !== null || customising;
  const codes = slots.filter((c) => c !== '');
  const problem = editing ? validateQuickButtons(codes, dispositions, teamId === null ? 'account' : 'team') : null;
  const dirty = codes.join(',') !== (saved ?? []).join(',');

  const save = async (next: string[]) => {
    setBusy(true);
    try {
      await onSave(teamId, next);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="knock-quick-buttons">
      <CardHeader>
        <CardTitle>{COPY.title}</CardTitle>
        <CardDescription>{COPY.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-xs space-y-1.5">
          <Label htmlFor="qb-scope">Applies to</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger id="qb-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ACCOUNT}>{COPY.scopeAccount}</SelectItem>
              {teams.map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>
                  {t.name}{quickButtonSetFor(rows, t.id) ? ' (own buttons)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!editing && (
          <div className="flex flex-col gap-3 rounded-md border border-dashed border-border p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">{COPY.usesAccount}</p>
            <Button size="sm" variant="outline" onClick={() => setCustomising(true)}>{COPY.customise}</Button>
          </div>
        )}

        {editing && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {slots.map((code, i) => (
              <div key={i} className="space-y-1.5">
                <Label htmlFor={`qb-slot-${i + 1}`}>{COPY.slot(i + 1)}</Label>
                <Select
                  value={code || EMPTY}
                  onValueChange={(v) => setSlots((s) => s.map((c, j) => (j === i ? (v === EMPTY ? '' : v) : c)))}
                >
                  <SelectTrigger id={`qb-slot-${i + 1}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EMPTY}>{COPY.empty}</SelectItem>
                    {choosable.map((d) => (
                      <SelectItem key={d.code} value={d.code} disabled={d.code !== code && slots.includes(d.code)}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
            <div className="space-y-1.5">
              <Label>{COPY.slot(QUICK_SLOTS + 1)}</Label>
              <div className="flex h-10 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
                {COPY.more}
              </div>
            </div>
          </div>
        )}

        {/* What the rep's Door sheet shows: a 2 by 3 grid, the sixth always More. */}
        <div aria-label="Preview" className="grid max-w-sm grid-cols-3 gap-2">
          {[...(editing ? codes : accountSet).slice(0, QUICK_SLOTS), '__more__'].map((c) => {
            const d = byCode.get(c);
            return (
              <div
                key={c}
                className="flex min-h-14 items-center justify-center rounded-lg border border-border px-2 text-center text-xs font-medium"
                style={d ? { borderColor: d.color, boxShadow: `inset 0 -4px 0 ${d.color}` } : undefined}
              >
                {d ? d.name : COPY.more}
              </div>
            );
          })}
        </div>

        {problem && (
          <Alert variant="destructive">
            <AlertDescription>{problem}</AlertDescription>
          </Alert>
        )}

        {editing && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => save(codes)} disabled={busy || problem !== null || (!dirty && saved !== null)}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {COPY.save}
            </Button>
            {teamId !== null && saved !== null && (
              <Button variant="outline" onClick={() => save([])} disabled={busy}>{COPY.revert}</Button>
            )}
            {teamId !== null && saved === null && (
              <Button variant="ghost" onClick={() => setCustomising(false)} disabled={busy}>Cancel</Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
